import { ChangeDetectionStrategy, Component, computed, inject, input, model, output, signal } from '@angular/core';
import { DsButtonModule, DsTooltipDirectiveModule } from '@bmw-ds/components';

import { CustomerContactLineComponent } from '../../../customer/components/customer-contact-line/customer-contact-line.component';
import { PersonalIdentificationNumbersComponent } from '../../../customer/components/personal-identification-numbers/personal-identification-numbers.component';
import { Customer } from '../../../customer/model/customer.model';
import { IdentityField } from '../../../customer/model/identity-field.model';
import { CustomerService } from '../../../customer/services/customer.service';
import { FeatureVisibilityService } from '../../../customer/services/feature-visibility.service';
import { useI18n } from '../../../i18n/services/i18n.service';
import { BmwMiniIdComponent } from '../bmw-mini-id/bmw-mini-id.component';
import { CreateBmwMiniIdWrapper } from '../create-bmw-mini-id/create-bmw-mini-id.wrapper';
import { DmsIdContainer } from '../dms-id/dms-id.container';

@Component({
  selector: 'cdf-profile-info-grid',
  imports: [
    CustomerContactLineComponent,
    DsButtonModule,
    BmwMiniIdComponent,
    CreateBmwMiniIdWrapper,
    PersonalIdentificationNumbersComponent,
    DsTooltipDirectiveModule,
    DmsIdContainer,
  ],
  template: `
    <div class="name-section container-fluid">
      <p class="name row justify-content-left title-md pb-0">
        {{ fullName() }}
      </p>
      <cdf-personal-identification-numbers
        class="row"
        [taxNumber]="customer().taxNumber"
        [nationalId1]="customer().nationalId1"
        [nationalId1DocumentType]="customer().nationalId1DocumentType"
      />
      @if (customer().gcid) {
        <cdf-bmw-mini-id
          class="row justify-content-left"
          [bmwOrMiniId]="customer().bmwMiniId"
          [readonly]="readonly()"
          (createBmwMiniId)="isCreateBmwMiniModalOpen.set(true)"
          [hidden]="featureVisibilityService.isHidden(IdentityField.BMW_MINI_ID)"
        />
      }
      <cdf-dms-id-container
        class="row"
        [dmsIds]="customer().dmsIds"
        [hidden]="featureVisibilityService.isHidden(IdentityField.DMS_ID)"
      />
    </div>
    <cdf-customer-contact-line [contact]="customer().privateContact" enableLinks />
    <ds-button
      variant="ghost"
      icon="edit"
      [disabled]="readonly()"
      [ds-tooltip]="i18n('readonlyTooltip')"
      [dsTooltipConfig]="{ disabled: !readonly(), origin: 'bottom', showTriggerDelay: 500 }"
      (click)="editCustomer.emit(customer())"
    />
    <cdf-customer-contact-line [contact]="customer().businessContact" business enableLinks />
    <cdf-create-bmw-mini-id-wrapper
      [(isModalOpen)]="isCreateBmwMiniModalOpen"
      [customer]="customer()"
      (successfullyInitializedIdCreation)="successfullyInitializedIdCreation.set(true)"
    />
  `,
  styleUrl: './profile-info-grid.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileInfoGridComponent {
  protected readonly customerService = inject(CustomerService);
  protected readonly i18n = useI18n();
  protected readonly featureVisibilityService = inject(FeatureVisibilityService);

  readonly customer = input.required<Customer>();
  readonly readonly = input(false);
  readonly successfullyInitializedIdCreation = model<boolean>();
  readonly editCustomer = output<Customer>();

  readonly isCreateBmwMiniModalOpen = signal(false);

  readonly fullName = computed(() => this.customerService.fullName(this.customer()));

  protected readonly IdentityField = IdentityField;
}
